'use client'

import {
  Button,
  Grid,
  Group,
  Paper,
  Stack,
  Stepper,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineTheme
} from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconAlertCircle,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconExclamationMark,
  IconInfoCircle
} from '@tabler/icons'
import { AxiosResponse } from 'axios'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { useSWRConfig } from 'swr'

import { CustomField } from '@/components/commons'
import {
  api,
  errorHandler,
  getStorage,
  notificationHandler,
  setStorage,
  Yup,
  yupSchemas
} from '@/lib/utils'
import {
  Form as FormType,
  FormField as FormFieldType,
  FormOption as FormOptionType,
  FormResponse,
  FormSection as FormSectionType
} from '@/types'

interface Props {
  data: {
    code?: string
    dateCreated: string
    dateSubmitted?: string
    dateUpdated: string
    form: FormType
    isPaginated?: boolean
    isRequired?: boolean
    status?: string
    statusDisplay?: string
    uid: string
    url?: string
  }
  siteSlug: string
  formSlug: string
  responseUid: string
  redirectToSuccessPage?: boolean
  onUpdate?: () => void
}

export default function FormResponseForm({
  data,
  siteSlug,
  formSlug,
  responseUid,
  redirectToSuccessPage = true,
  onUpdate
}: Props) {
  // Hooks
  const router = useRouter()
  const { mutate: mutateGlobal } = useSWRConfig()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`)

  // Constants
  const formOwnerSubdomain = data?.form?.siteSubdomain || siteSlug
  const formResponseUpdateUrl = `/${formOwnerSubdomain}/forms/${formSlug}/responses/${responseUid}/`
  const sections: FormSectionType[] =
    data?.form?.sections
      ?.filter(
        section =>
          section.fields?.filter((field: FormFieldType) => field.isActive && !field.isHidden).length
      )
      ?.sort((a: FormSectionType, b: FormSectionType) => a.ordering - b.ordering) || []

  const isPaginated = data?.form?.isPaginated

  const values: any = {}
  const yupSchema: any = {}
  sections.forEach(section => {
    section.fields?.forEach((field: any) => {
      if (field.type === 'bool') values[field.uid] = field.value || false
      else if (field.type === 'select') values[field.uid] = field.value?.uid || ''
      else values[field.uid] = field.value
      yupSchemas.createYupSchemaFormResponse(yupSchema, field)
    })
  })

  // States
  const [loading, setLoading] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [visitedSections, setVisitedSections] = useState<Set<number>>(new Set([0]))

  // Constants
  const initialValues = { ...values }
  const schema = Yup.object().shape(yupSchema)
  const totalPages = sections.length
  const currentSection = sections[currentPage - 1]

  // Mantine form
  const form = useForm({
    initialValues,
    validate: yupResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true
  })

  // Actions
  const isAddressField = (fieldUid: string) =>
    sections.some(section =>
      section.fields?.some(field => field.uid === fieldUid && field.type === 'address')
    )

  const isAddressFieldValueSavable = (fieldValue: any) => {
    if (!fieldValue || typeof fieldValue !== 'object') return false
    return Boolean(
      (fieldValue?.cityUid || fieldValue?.city) && (fieldValue?.stateCode || fieldValue?.state)
    )
  }

  const getFormValues = () => {
    return Object.fromEntries(
      Object.entries(form.values).filter(([key, value]) => {
        if (!(typeof value === 'boolean' || (typeof value !== 'boolean' && form.isDirty(key)))) {
          return false
        }

        if (value === null) return false

        if (isAddressField(key)) return isAddressFieldValueSavable(value)

        return (
          typeof value !== 'string' || (typeof value === 'string' && value.indexOf('base64') === -1)
        )
      })
    )
  }

  const getUploadValues = () => {
    return Object.fromEntries(
      Object.entries(form.values).filter(
        ([key, value]) =>
          form.isDirty(key) &&
          value !== null &&
          typeof value === 'string' &&
          value.indexOf('base64') !== -1
      )
    )
  }

  const isAddressFieldValueIncomplete = (fieldUid: string, fieldValue: any) => {
    if (!isAddressField(fieldUid) || !fieldValue || typeof fieldValue !== 'object') return false
    const hasPostcode = `${fieldValue?.postcode || ''}`.trim() !== ''
    if (!hasPostcode) return false

    const hasCityAndState = isAddressFieldValueSavable(fieldValue)

    return !hasCityAndState
  }

  const hasIncompleteAddressForDraft = () =>
    Object.entries(form.values).some(([fieldUid, fieldValue]) =>
      isAddressFieldValueIncomplete(fieldUid, fieldValue)
    )

  const clearIncompleteAddressPostcodes = () => {
    const updatedValues: Record<string, any> = {}
    let hasChanges = false

    Object.entries(form.values).forEach(([fieldUid, fieldValue]) => {
      if (isAddressFieldValueIncomplete(fieldUid, fieldValue)) {
        updatedValues[fieldUid] = { ...fieldValue, postcode: '' }
        hasChanges = true
      }
    })

    if (hasChanges) {
      form.setValues({ ...form.values, ...updatedValues })
    }
  }

  const updateFormData = async (values: any) => {
    return api.patch(formResponseUpdateUrl, { fields: { ...values } })
  }

  const uploadFiles = async (uploadValues: any) => {
    const uploadPromises = Object.entries(uploadValues).map(([key, value]) =>
      api.patch(formResponseUpdateUrl, { fields: { [key]: value } }).catch(error => error?.response)
    )

    const requests: AxiosResponse<FormResponse>[] = await Promise.all(uploadPromises)

    const failedRequests = requests.filter(
      (request: AxiosResponse<FormResponse>) =>
        request?.status && ![200, 201].includes(request?.status)
    )

    if (failedRequests.length > 0) {
      throw new Error(`Erro no envio de ${failedRequests.length} arquivo(s)`)
    }

    return requests
  }

  const handleSuccess = () => {
    onUpdate?.()
    mutateGlobal(formResponseUpdateUrl)
    notificationHandler({ message: 'Dados salvos com sucesso!', variant: 'success' })
  }

  const handleError = (error: any, defaultMessage: string) => {
    const errors = errorHandler(error?.response?.data)
    notificationHandler({
      message: `${errors?.messages?.[0]}` || defaultMessage,
      variant: 'error'
    })
    throw error
  }

  const handleUpdate = async (notification: boolean = true) => {
    try {
      const newValues = getFormValues()
      const uploadValues = getUploadValues()

      Object.keys(uploadValues).length > 0 && (await uploadFiles(uploadValues))
      await updateFormData(newValues)

      form.resetDirty()
      notification && handleSuccess()
      onUpdate?.()
    } catch (error) {
      handleError(error, 'Erro ao atualizar os dados.')
    }
  }

  function hasDraftableChanges() {
    const formValues = getFormValues()
    const uploadValues = getUploadValues()
    return Object.keys(formValues).length > 0 || Object.keys(uploadValues).length > 0
  }

  const handleAutoSave = async () => {
    clearIncompleteAddressPostcodes()
    if (hasDraftableChanges() && !hasIncompleteAddressForDraft()) {
      try {
        await handleUpdate(false)
      } catch (error) {
        handleError(error, 'Erro no salvamento automático.')
      }
    }
  }

  const handleSaveDraft = () => {
    clearIncompleteAddressPostcodes()
    if (!hasDraftableChanges()) return
    setLoading(true)
    handleUpdate().finally(() => setLoading(false))
  }

  const handleSubmit = async () => {
    setLoading(true)

    try {
      await handleUpdate()

      await api.post(`${formResponseUpdateUrl}submit/`)
      onUpdate?.()

      const userForms: Record<string, string> = getStorage('user-forms') || {}
      const { [formSlug as string]: removedForm, ...updatedForms } = userForms
      setStorage('user-forms', updatedForms)
      if (redirectToSuccessPage) router.push(`/forms/${formSlug}/success`)
    } catch (error) {
      handleError(
        error,
        'Erro ao enviar o formulário. Verifique se foi preenchido corretamente ou tente novamente mais tarde.'
      )
    } finally {
      setLoading(false)
    }
  }

  // Pagination
  const getFieldValidation = (field: FormFieldType, fieldValue: any) => {
    if (field.type === 'bool') return typeof fieldValue === 'boolean'
    if (field.type === 'select') return fieldValue && fieldValue !== '' && fieldValue !== null
    return fieldValue && fieldValue.toString().trim() !== '' && fieldValue !== null
  }

  const isSectionComplete = (section: FormSectionType) => {
    const sectionFields =
      section.fields?.filter((field: FormFieldType) => field.isActive && !field.isHidden) || []

    if (sectionFields.length === 0) return true

    return sectionFields.every(field => {
      const fieldError = form.errors[field.uid]
      const fieldValue = form.values[field.uid]

      if (fieldError) return false

      const isValidValue = getFieldValidation(field, fieldValue)

      if (field.isRequired) return isValidValue

      return fieldValue === null || fieldValue === undefined || fieldValue === '' || isValidValue
    })
  }

  const hasSectionBeenVisited = (sectionIndex: number) => visitedSections.has(sectionIndex)

  const isSectionCompleted = (sectionIndex: number) => {
    const section = sections[sectionIndex]
    return section ? hasSectionBeenVisited(sectionIndex) && isSectionComplete(section) : false
  }

  const isFieldEmpty = (field: FormFieldType, fieldValue: any) => {
    if (field.type === 'bool') return typeof fieldValue !== 'boolean'
    if (field.type === 'select') return !fieldValue || fieldValue === '' || fieldValue === null
    return !fieldValue || fieldValue.toString().trim() === '' || fieldValue === null
  }

  const hasRequiredFieldsNotFilled = (sectionIndex: number) => {
    const section = sections[sectionIndex]
    if (!section || !hasSectionBeenVisited(sectionIndex) || currentPage === sectionIndex + 1) {
      return false
    }

    const sectionFields =
      section.fields?.filter((field: FormFieldType) => field.isActive && !field.isHidden) || []

    return sectionFields.some(field => {
      if (!field.isRequired) return false

      const fieldValue = form.values[field.uid]
      const fieldError = form.errors[field.uid]

      if (fieldError) return true

      return isFieldEmpty(field, fieldValue)
    })
  }

  const handleSectionClick = async (sectionIndex: number) => {
    await handleAutoSave()
    setCurrentPage(sectionIndex + 1)
    setVisitedSections(prev => new Set([...prev, sectionIndex]))
  }

  const handlePreviousPage = async () =>
    currentPage > 1 && (await handleAutoSave(), setCurrentPage(currentPage - 1))

  const handleNextPage = async () =>
    currentPage < totalPages &&
    (await handleAutoSave(),
    setCurrentPage(currentPage + 1),
    setVisitedSections(prev => new Set([...prev, currentPage])))

  const areAllSectionsCompleted = () => sections.every((__, index) => isSectionCompleted(index))

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} style={{ width: '100%' }}>
      {isPaginated ? (
        <>
          {isMobile ? (
            <Stack spacing="md">
              <Paper withBorder p="md">
                <Stepper
                  active={currentPage - 1}
                  orientation="horizontal"
                  size="sm"
                  styles={{
                    root: {
                      width: '100%',
                      maxWidth: '100%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: sections.length > 6 ? 'start' : 'center',
                      overflowX: sections.length > 6 ? 'auto' : 'unset'
                    },
                    step: {
                      cursor: 'default',
                      margin: '0 5px'
                    },
                    stepBody: { display: 'none' },
                    stepDescription: { display: 'none' },
                    stepLabel: { display: 'none' },
                    separator: { display: 'none' }
                  }}>
                  {sections.map((section, index) => {
                    const isComplete = isSectionCompleted(index)
                    const hasRequiredNotFilled = hasRequiredFieldsNotFilled(index)

                    return (
                      <Stepper.Step
                        key={section.uid}
                        completedIcon={
                          isComplete ? (
                            <IconCheck size={14} />
                          ) : hasRequiredNotFilled ? (
                            <IconExclamationMark size={14} />
                          ) : undefined
                        }
                        color={hasRequiredNotFilled ? 'red' : isComplete ? 'green' : 'custom'}
                      />
                    )
                  })}
                </Stepper>{' '}
              </Paper>

              {currentSection && (
                <Paper withBorder p="md">
                  <Title order={4}>{currentSection.title}</Title>
                  <Text size={14} mb={15}>
                    {currentSection.description}
                  </Text>

                  <Stack spacing="sm">
                    {currentSection.fields
                      ?.filter((field: FormFieldType) => field.isActive && !field.isHidden)
                      ?.sort((a: FormFieldType, b: FormFieldType) => a.ordering - b.ordering)
                      ?.map((field: FormFieldType) => {
                        const { config, isEditable, options = [], value, ...restField } = field
                        const { many, ...restConfig } = config || {}
                        return (
                          <CustomField.Input
                            key={field.uid}
                            value={form.values?.[field.uid] || ''}
                            showLabel
                            config={{ ...restConfig, multiple: many }}
                            options={options.map((option: FormOptionType) => ({
                              label: option.name,
                              value: option.uid
                            }))}
                            {...restField}
                            error={form.errors[field.uid]}
                            onChange={(newValue: any) => form.setFieldValue(field.uid, newValue)}
                            inputProps={{ disabled: loading || !isEditable }}
                          />
                        )
                      })}
                  </Stack>

                  <Stack spacing="sm" mt="md" align="center">
                    <Text size="sm" weight={500}>
                      {currentPage} de {totalPages}
                    </Text>
                  </Stack>
                </Paper>
              )}

              <Group position="center" align="center" spacing="xl">
                <Button
                  type="button"
                  variant="filled"
                  color="custom"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={handlePreviousPage}
                  leftIcon={<IconChevronLeft size={16} />}>
                  Anterior
                </Button>

                <Button
                  type="button"
                  variant="filled"
                  color="custom"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={handleNextPage}
                  rightIcon={<IconChevronRight size={16} />}>
                  Próximo
                </Button>
              </Group>
              <Button
                type="button"
                color="orange"
                loading={loading}
                disabled={loading || !hasDraftableChanges()}
                onClick={() => {
                  handleSaveDraft()
                }}
                fullWidth>
                Salvar Rascunho
              </Button>
              <Button
                type="button"
                color="green"
                loading={loading}
                disabled={loading || !form.isValid() || !areAllSectionsCompleted()}
                onClick={handleSubmit}
                fullWidth>
                Enviar
              </Button>
            </Stack>
          ) : (
            <Grid gutter="lg">
              <Grid.Col span={8}>
                <Stack align="center">
                  {currentSection && (
                    <Paper withBorder p="md" w="100%">
                      <Title order={4}>{currentSection.title}</Title>
                      <Text size={14} mb={15}>
                        {currentSection.description}
                      </Text>

                      <Stack spacing="sm">
                        {currentSection.fields
                          ?.filter((field: FormFieldType) => field.isActive && !field.isHidden)
                          ?.sort((a: FormFieldType, b: FormFieldType) => a.ordering - b.ordering)
                          ?.map((field: FormFieldType) => {
                            const { config, isEditable, options = [], value, ...restField } = field
                            const { many, ...restConfig } = config || {}
                            return (
                              <CustomField.Input
                                key={field.uid}
                                value={form.values?.[field.uid] || ''}
                                showLabel
                                config={{ ...restConfig, multiple: many }}
                                options={options.map((option: FormOptionType) => ({
                                  label: option.name,
                                  value: option.uid
                                }))}
                                {...restField}
                                error={form.errors[field.uid]}
                                onChange={(newValue: any) =>
                                  form.setFieldValue(field.uid, newValue)
                                }
                                inputProps={{ disabled: loading || !isEditable }}
                              />
                            )
                          })}
                      </Stack>

                      <Group position="apart" mt="lg">
                        <Button
                          variant="outline"
                          color="custom"
                          size="sm"
                          disabled={currentPage === 1}
                          onClick={handlePreviousPage}
                          leftIcon={<IconChevronLeft size={16} />}>
                          Anterior
                        </Button>

                        <Text size="sm" weight={500} color="dimmed">
                          {currentPage} de {totalPages}
                        </Text>

                        {currentPage !== totalPages ? (
                          <Button
                            variant="outline"
                            color="custom"
                            size="sm"
                            disabled={currentPage === totalPages}
                            onClick={handleNextPage}
                            rightIcon={<IconChevronRight size={16} />}>
                            Próximo
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            color="green"
                            size="sm"
                            loading={loading}
                            disabled={loading || !form.isValid() || !areAllSectionsCompleted()}
                            onClick={handleSubmit}
                            w={125}>
                            Enviar
                          </Button>
                        )}
                      </Group>
                    </Paper>
                  )}
                </Stack>
              </Grid.Col>

              <Grid.Col span={4}>
                <Paper withBorder p="md">
                  <Stack spacing="lg">
                    {totalPages > 1 && (
                      <Stack spacing="sm">
                        {sections.map((section, index) => {
                          const isActive = currentPage === index + 1
                          const isComplete = isSectionCompleted(index)
                          const hasRequiredNotFilled = hasRequiredFieldsNotFilled(index)

                          return (
                            <Group
                              key={section.uid}
                              position="apart"
                              align="center"
                              p="sm"
                              style={{
                                cursor: 'pointer',
                                borderRadius: '4px',
                                backgroundColor: isActive ? '#f8f9fa' : 'transparent',
                                transition: 'background-color 0.2s ease'
                              }}
                              onMouseEnter={e => {
                                if (!isActive) {
                                  e.currentTarget.style.backgroundColor = '#f1f3f4'
                                }
                              }}
                              onMouseLeave={e => {
                                if (!isActive) {
                                  e.currentTarget.style.backgroundColor = 'transparent'
                                }
                              }}
                              onClick={() => handleSectionClick(index)}>
                              <Group spacing="sm" align="center">
                                <ThemeIcon
                                  size="sm"
                                  radius="xl"
                                  color={
                                    isComplete
                                      ? 'green'
                                      : hasRequiredNotFilled
                                        ? 'red'
                                        : isActive
                                          ? 'custom'
                                          : 'custom'
                                  }
                                  variant={isComplete ? 'filled' : 'filled'}>
                                  {isComplete ? (
                                    <IconCheck size={16} />
                                  ) : hasRequiredNotFilled ? (
                                    <IconAlertCircle size={16} />
                                  ) : (
                                    <Text size="sm" weight={500} color="white">
                                      {index + 1}
                                    </Text>
                                  )}
                                </ThemeIcon>
                                <Text
                                  weight={isActive ? 700 : 400}
                                  size="sm"
                                  color={hasRequiredNotFilled ? 'red' : undefined}>
                                  {section.title}
                                </Text>
                              </Group>
                              {section.description && (
                                <Tooltip label={section.description} withArrow color="custom">
                                  <ThemeIcon variant="light" color="gray" size="sm">
                                    <IconInfoCircle size={16} />
                                  </ThemeIcon>
                                </Tooltip>
                              )}
                            </Group>
                          )
                        })}
                      </Stack>
                    )}

                    <Stack spacing="sm">
                      <Button
                        type="button"
                        color="orange"
                        loading={loading}
                        disabled={loading || !hasDraftableChanges()}
                        onClick={() => {
                          handleSaveDraft()
                        }}
                        fullWidth>
                        Salvar Rascunho
                      </Button>
                      <Button
                        type="button"
                        color="green"
                        loading={loading}
                        disabled={loading || !form.isValid() || !areAllSectionsCompleted()}
                        onClick={handleSubmit}
                        fullWidth>
                        Enviar
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          )}
        </>
      ) : (
        <Stack align="center">
          {sections.map((section: FormSectionType) => {
            const fields: FormFieldType[] =
              section.fields
                ?.filter((field: FormFieldType) => field.isActive && !field.isHidden)
                ?.sort((a: FormFieldType, b: FormFieldType) => a.ordering - b.ordering) || []
            return (
              <Paper withBorder key={section.title} p="md" w="100%">
                <Title order={4}>{section.title}</Title>
                <Text size={14} mb={15}>
                  {section.description}
                </Text>

                <Stack spacing="sm">
                  {fields.map((field: FormFieldType) => {
                    const { config, isEditable, options = [], value, ...restField } = field
                    const { many, ...restConfig } = config || {}
                    return (
                      <CustomField.Input
                        key={field.uid}
                        value={form.values?.[field.uid] || ''}
                        showLabel
                        config={{ ...restConfig, multiple: many }}
                        options={options.map((option: FormOptionType) => ({
                          label: option.name,
                          value: option.uid
                        }))}
                        {...restField}
                        error={form.errors[field.uid]}
                        onChange={(newValue: any) => form.setFieldValue(field.uid, newValue)}
                        inputProps={{ disabled: loading || !isEditable }}
                      />
                    )
                  })}
                </Stack>
              </Paper>
            )
          })}

          <Group>
            <Button
              type="button"
              color="orange"
              loading={loading}
              disabled={loading || !hasDraftableChanges()}
              onClick={() => {
                handleSaveDraft()
              }}>
              Salvar Rascunho
            </Button>
            <Button
              type="button"
              color="green"
              loading={loading}
              disabled={loading || !form.isValid()}
              onClick={handleSubmit}>
              Enviar
            </Button>
          </Group>
        </Stack>
      )}
    </form>
  )
}
